import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled, { DefaultTheme, withTheme } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import FocusLock from 'react-focus-lock';
import Button from '../../components/atoms/Button/Button';
import Input from '../../components/atoms/Input/Input';
import { searchValidationConfig } from '../../types/ValidationTypes';
import SearchTerms from '../../components/molecules/Paylink/SearchTerms';

import { useAppState } from '../../store/appstate';
import {
    createEncodedSearchQueryString,
    parseSearchUrl,
    removeQuoteSearch,
    generateQuoteSearchEscapeQuotesInContent,
} from '../../services/helpers/queryStringHelpers';
import CheckBox from '../../components/atoms/Checkbox/Checkbox';
import { setPerPage } from '../../store/userSettings/userSettingsSlice';
import { useAppDispatch } from '../../store';
import { ConnectedStore } from '../../types/ConnectedStore';
import { searchPaylinks, searchPaylinksOld } from '../../api/paylinks/paylinks';
import { usePaylinksStore } from '../../store/checkout/paylinksStore';
import Flex from '../../components/atoms/Box/Flex';
import { getStoresAvailableForUser } from '../../services/helpers/storeHelper';
import { useIsFlagEnabled } from '../../services/helpers/isFlagEnabled';

interface Props {
    theme: DefaultTheme;
}

type Inputs = {
    searchPhrase: string;
    filter: string[];
};

const SearchForm = ({ theme }: Props) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const {
        register,
        handleSubmit,
        formState: { errors },
        getValues,
    } = useForm<Inputs>();

    const {
        isSearching,
        searchCancelToken,
        exactSearch,
        searchStarted,
        searchSucceeded,
        searchFailed,
        searchCanceled,
        setExactSearch,
    } = usePaylinksStore();

    const user = useAppState(s => s.session.user);
    let availableStores = useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? [];

    availableStores = getStoresAvailableForUser(availableStores, user?.filteredStores);

    const { pathname, search } = useLocation();
    const { page, sort, query, perPage, filterMap: filter } = parseSearchUrl(search);
    const refreshDefaultPerPageFlag = useIsFlagEnabled('mx-39bc158c-4bfb-4b88-8923-c867999f3ac8');

    const SEARCH_PHRASE = 'searchPhrase';
    const searchPhrase = register(SEARCH_PHRASE, searchValidationConfig);

    const onFilterChanged = (filters: Map<string, string[]>) => {
        navigate(
            `${pathname}?${createEncodedSearchQueryString(
                query,
                filters,
                sort,
                perPage,
                [],
                '1',
                undefined
            )}`
        );
    };

    useEffect(() => {
        performSearch(query, exactSearch, page, false);
    }, [page, JSON.stringify(Array.from(filter.entries()))]);

    const onSubmit = (form: Inputs) => {
        performSearch(form.searchPhrase, exactSearch, form.searchPhrase === query ? page : '1');
    };

    const onSetExactMatch = () => {
        const searchPhrase = getValues(SEARCH_PHRASE);
        setExactSearch(!exactSearch);
        performSearch(searchPhrase, !exactSearch, '1');
    };

    useEffect(() => {
        dispatch(setPerPage(perPage));
    }, [dispatch]);

    const performSearch = (query: string, exact: boolean, p: string, nav: boolean = true) => {
        if (isSearching && searchCancelToken) searchCancelToken.cancel();
        const withOrWithoutQuotes = exact
            ? generateQuoteSearchEscapeQuotesInContent(query)
            : removeQuoteSearch(query);

        const newQuery = createEncodedSearchQueryString(
            withOrWithoutQuotes,
            filter,
            sort,
            perPage,
            [],
            p
        );

        nav && navigate(`${pathname}?${newQuery}`);

        if (!refreshDefaultPerPageFlag) {
            searchPaylinksOld(
                newQuery,
                searchStarted,
                searchSucceeded,
                searchFailed,
                searchCanceled
            );
            return;
        }

        searchPaylinks(newQuery, searchStarted, searchSucceeded, searchFailed, searchCanceled);
    };

    return (
        <Flex column gap={theme.layout.gap.xsmall}>
            <form onSubmit={handleSubmit(onSubmit)} data-testid="form">
                <SearchContainer>
                    <InputWrapper>
                        <FocusLock disabled={!isSearching}>
                            <Input
                                data-testid="search-input"
                                type="text"
                                name={searchPhrase.name}
                                defaultValue={removeQuoteSearch(query)}
                                setRef={searchPhrase.ref}
                                placeholder="What are you looking for?"
                                onChange={searchPhrase.onChange}
                                id="paylink-search"
                            />
                        </FocusLock>
                    </InputWrapper>
                    <Button type="submit" primary large>
                        Search
                    </Button>
                    <CheckBox
                        dataTestId="paylinksExactMatch"
                        checked={exactSearch}
                        onChange={onSetExactMatch}
                    >
                        Exact match
                    </CheckBox>
                </SearchContainer>
            </form>
            <SearchTerms
                filter={filter}
                stores={availableStores}
                onFilterChanged={onFilterChanged}
            />
            {errors.searchPhrase && (
                <p className="error-message" role="alert">
                    {errors.searchPhrase?.message}
                </p>
            )}
        </Flex>
    );
};

const SearchContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.layout.gap.small};
`;

const InputWrapper = styled.div`
    width: 40rem;
`;

export default withTheme(SearchForm) as React.ComponentType<Omit<Props, 'theme'>>;
